import { useForm } from 'react-hook-form';

import { ApiAddress, EntityCreateBusinessEntityRequest, EntityCreatePersonEntityRequest } from '~/typings/API';

export enum EntityFormType {
  Person = 'person',
  Business = 'business',
}

export type IdentificationDocument = NonNullable<EntityCreatePersonEntityRequest['passport']>;

export interface PersonEntityFormValues {
  id?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  ssn?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: string;
  address?: ApiAddress;
  isRoot?: boolean;
  passport?: IdentificationDocument;
  driversLicense?: IdentificationDocument;
  nationalId?: IdentificationDocument;
  temporaryData?: { isCitizen?: boolean };
}

export interface BeneficialOwnerFormValues extends PersonEntityFormValues {
  isControlPerson?: boolean;
  isBeneficialOwner?: boolean;
  ownershipPercentage?: number | string;
  jobTitle?: string;
}

export interface BusinessEntityFormValues extends Omit<EntityCreateBusinessEntityRequest, 'beneficialOwners'> {
  id?: string;
  beneficialOwners?: BeneficialOwnerFormValues[];
}

const createEmptyAddress = (): ApiAddress => ({
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: '',
});

const trimValue = (value?: string | null) => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.toString().trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeAddress = (address?: ApiAddress): ApiAddress | undefined => {
  if (!address) return undefined;
  const line1 = trimValue(address.line1);
  const line2 = trimValue(address.line2);
  const city = trimValue(address.city);
  const state = trimValue(address.state);
  const postalCode = trimValue(address.postalCode);
  const countryCode = trimValue(address.countryCode);

  if (!line1 && !line2 && !city && !state && !postalCode && !countryCode) return undefined;

  return {
    ...(line1 && { line1 }),
    ...(line2 && { line2 }),
    ...(city && { city }),
    ...(state && { state }),
    ...(postalCode && { postalCode }),
    ...(countryCode && { countryCode }),
  } as ApiAddress;
};

const createEmptyIdentification = (): IdentificationDocument => ({
  // eslint-disable-next-line id-blacklist
  number: '',
  countryCode: '',
});

const normalizeIdentification = (doc?: IdentificationDocument) => {
  if (!doc) return undefined;
  const docNumber = trimValue(doc.number);
  const countryCode = trimValue(doc.countryCode);

  if (!docNumber && !countryCode) return undefined;

  return {
    // eslint-disable-next-line id-blacklist
    ...(docNumber && { number: docNumber }),
    ...(countryCode && { countryCode }),
  };
};

export const normalizeDateOfBirth = (date: unknown): string | undefined => {
  if (typeof date !== 'string') return undefined;
  const trimmed = date.trim();
  if (!trimmed) return undefined;
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined;
};

const inferCitizenFromIdentifiers = (entry: Partial<BeneficialOwnerFormValues | PersonEntityFormValues>): boolean => {
  if (entry.ssn && typeof entry.ssn === 'string' && entry.ssn.trim()) {
    return true;
  }
  if (
    (entry.passport && entry.passport.number) ||
    (entry.driversLicense && entry.driversLicense.number) ||
    (entry.nationalId && entry.nationalId.number)
  ) {
    return false;
  }
  return true;
};

export const createDefaultPersonFormValues = (): PersonEntityFormValues => ({
  id: undefined,
  firstName: '',
  middleName: '',
  lastName: '',
  ssn: '',
  dateOfBirth: '',
  email: '',
  phoneNumber: '',
  passport: createEmptyIdentification(),
  driversLicense: createEmptyIdentification(),
  nationalId: createEmptyIdentification(),
  address: createEmptyAddress(),
  temporaryData: { isCitizen: true },
});

export const createDefaultBeneficialOwner = (): BeneficialOwnerFormValues => ({
  ...createDefaultPersonFormValues(),
  temporaryData: { isCitizen: true },
  isControlPerson: undefined,
  isBeneficialOwner: true,
  ownershipPercentage: undefined,
  jobTitle: '',
});

export const createDefaultBusinessFormValues = (): BusinessEntityFormValues => ({
  id: undefined,
  businessName: '',
  ein: '',
  website: '',
  address: createEmptyAddress(),
  beneficialOwners: [],
});

export const createInitialBusinessFormState = (): BusinessEntityFormValues => ({
  ...createDefaultBusinessFormValues(),
  beneficialOwners: [],
});

const buildPersonBaseRequest = (
  values: PersonEntityFormValues
): Omit<
  EntityCreatePersonEntityRequest,
  'isControlPerson' | 'isBeneficialOwner' | 'ownershipPercentage' | 'jobTitle'
> => {
  const isCitizen = values.temporaryData?.isCitizen !== false;

  const firstName = trimValue(values.firstName);
  const middleName = trimValue(values.middleName);
  const lastName = trimValue(values.lastName);
  const ssn = isCitizen ? trimValue(values.ssn) : undefined;
  const dateOfBirth = normalizeDateOfBirth(values.dateOfBirth);
  const email = trimValue(values.email);
  const phoneNumber = trimValue(values.phoneNumber);
  const address = normalizeAddress(values.address);
  const passport = !isCitizen ? normalizeIdentification(values.passport) : undefined;
  const driversLicense = !isCitizen ? normalizeIdentification(values.driversLicense) : undefined;
  const nationalId = !isCitizen ? normalizeIdentification(values.nationalId) : undefined;

  return {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    ...(middleName && { middleName }),
    ...(ssn && { ssn }),
    ...(dateOfBirth && { dateOfBirth }),
    ...(email && { email }),
    ...(phoneNumber && { phoneNumber }),
    ...(address && { address }),
    ...(passport && { passport }),
    ...(driversLicense && { driversLicense }),
    ...(nationalId && { nationalId }),
  };
};

const buildBeneficialOwnerBaseRequest = (values: BeneficialOwnerFormValues): EntityCreatePersonEntityRequest => {
  const baseRequest = buildPersonBaseRequest(values);

  const isControlPerson = values.isControlPerson;
  const isBeneficialOwner = values.isBeneficialOwner;

  let ownershipPercentage: number | undefined;
  if (values.ownershipPercentage !== undefined && values.ownershipPercentage !== null) {
    const numValue =
      typeof values.ownershipPercentage === 'string'
        ? values.ownershipPercentage.trim() === ''
          ? undefined
          : Number(values.ownershipPercentage)
        : Number(values.ownershipPercentage);

    if (numValue !== undefined && !isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      ownershipPercentage = numValue;
    }
  }

  const jobTitle = trimValue(values.jobTitle);

  return {
    ...baseRequest,
    ...(isControlPerson !== undefined && { isControlPerson }),
    ...(isBeneficialOwner !== undefined && { isBeneficialOwner }),
    ...(ownershipPercentage !== undefined && { ownershipPercentage }),
    ...(jobTitle && { jobTitle }),
  };
};

export const buildBeneficialOwnerRequest = (
  values: BeneficialOwnerFormValues
): EntityCreatePersonEntityRequest | undefined => {
  const request = buildBeneficialOwnerBaseRequest(values);
  return Object.keys(request).length > 0 ? request : undefined;
};

export const buildPersonRequest = (values: PersonEntityFormValues): EntityCreatePersonEntityRequest =>
  buildPersonBaseRequest(values) as EntityCreatePersonEntityRequest;

export const buildBusinessRequest = (values: BusinessEntityFormValues): EntityCreateBusinessEntityRequest => {
  const beneficialOwners =
    values.beneficialOwners
      ?.map((owner) => buildBeneficialOwnerRequest(owner))
      .filter((owner): owner is EntityCreatePersonEntityRequest => Boolean(owner)) ?? [];

  const businessName = trimValue(values.businessName);
  const ein = trimValue(values.ein);
  const website = trimValue(values.website);
  const address = normalizeAddress(values.address);

  return {
    businessName: businessName ?? '',
    ein: ein ?? '',
    ...(website && { website }),
    address: address ?? createEmptyAddress(),
    ...(beneficialOwners.length > 0 && { beneficialOwners }),
  };
};

export const applyPersonDefaults = (values: Partial<PersonEntityFormValues>): PersonEntityFormValues => {
  const base = createDefaultPersonFormValues();
  const merged: PersonEntityFormValues = {
    ...base,
    ...values,
    address: { ...base.address, ...(values.address ?? {}) },
    passport: { ...base.passport, ...(values.passport ?? {}) },
    driversLicense: { ...base.driversLicense, ...(values.driversLicense ?? {}) },
    nationalId: { ...base.nationalId, ...(values.nationalId ?? {}) },
  };

  merged.temporaryData = { isCitizen: inferCitizenFromIdentifiers(merged) };

  return merged;
};

export const applyBeneficialOwnerDefaults = (owner: Partial<BeneficialOwnerFormValues>): BeneficialOwnerFormValues => {
  const base = createDefaultBeneficialOwner();
  const merged: BeneficialOwnerFormValues = {
    ...base,
    ...owner,
    address: { ...base.address, ...(owner.address ?? {}) },
    passport: { ...base.passport, ...(owner.passport ?? {}) },
    driversLicense: { ...base.driversLicense, ...(owner.driversLicense ?? {}) },
    nationalId: { ...base.nationalId, ...(owner.nationalId ?? {}) },
  };

  merged.temporaryData = { isCitizen: inferCitizenFromIdentifiers(merged) };

  return merged;
};

export const applyBusinessDefaults = (values: Partial<BusinessEntityFormValues>): BusinessEntityFormValues => {
  const base = createDefaultBusinessFormValues();
  const merged: BusinessEntityFormValues = {
    ...base,
    ...values,
    address: { ...base.address, ...(values.address ?? {}) },
    beneficialOwners:
      values.beneficialOwners && values.beneficialOwners.length > 0
        ? values.beneficialOwners.map((owner) => applyBeneficialOwnerDefaults(owner))
        : [],
  };

  return merged;
};

export type EntityFormValues = PersonEntityFormValues | BusinessEntityFormValues;

export const createDefaultValuesForType = (type: EntityFormType): EntityFormValues =>
  type === EntityFormType.Person ? createDefaultPersonFormValues() : createDefaultBusinessFormValues();

export const useEntityForm = (type: EntityFormType = EntityFormType.Person, defaultValues?: EntityFormValues) =>
  useForm<EntityFormValues>({
    mode: 'onChange',
    defaultValues: defaultValues ?? createDefaultValuesForType(type),
    shouldUnregister: false,
  });
